import dragula from "dragula";

function characterLength(value) {
  return Array.from(String(value || "")).length;
}

export function initStoreProductForm() {
  const form = document.getElementById("productForm");
  if (!form) {
    return;
  }

  [
    initApartmentLayouts,
    initShortDescriptionCounter,
    initPublicationSchedule,
    initPublicationTimezoneAssistant,
    initProductEditorSections,
    initMobileProductAccordions,
    initProductEditorState,
    initProductTypeVisibility,
    initProductMediaManager,
    initCatalogPosition,
  ].forEach((initializer) => {
    try {
      initializer(form);
    } catch (error) {
      console.error(error);
    }
  });
}

function initApartmentLayouts(form) {
  const root = form.querySelector("[data-apartment-layouts]");
  const categorySelect = form.querySelector("#platform_category_id");
  if (!root || !categorySelect || root.dataset.layoutsReady === "1") {
    return;
  }
  root.dataset.layoutsReady = "1";

  const list = root.querySelector("[data-layout-list]");
  const addButton = root.querySelector("[data-layout-add]");
  const empty = root.querySelector("[data-layout-empty]");
  const configNode = document.getElementById("apartment-layouts-config")
    || root.querySelector("[data-layout-config]");
  let config = {};
  try {
    config = JSON.parse(configNode?.textContent || "{}");
  } catch (error) {
    config = {};
  }
  const i18n = config.i18n || {};
  const targetCategoryIds = new Set((config.target_category_ids || []).map(String));
  const maxLayouts = Number.parseInt(config.max_layouts || "100", 10);
  const maxImageBytes = Number.parseInt(config.max_image_bytes || String(8 * 1024 * 1024), 10);
  let newCardSequence = 0;
  let warnedForCategoryChange = false;
  let draggedCard = null;

  const text = (key, fallback = "") => String(i18n[key] || fallback || key);
  const escapeHtml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

  const cards = () => Array.from(list?.querySelectorAll("[data-layout-card]") || []);
  const isSupportedCategory = () => {
    const selectedOption = categorySelect.options?.[categorySelect.selectedIndex] || null;
    const selectedSlug = String(selectedOption?.dataset?.categorySlug || "").toLowerCase();
    const targetSlug = String(config.target_category_slug || "novostroyka").toLowerCase();
    return targetCategoryIds.has(String(categorySelect.value || ""))
      || selectedOption?.dataset?.apartmentLayoutsCategory === "1"
      || (selectedSlug !== "" && selectedSlug === targetSlug);
  };
  const currency = () => String(form.querySelector("[name='currency']")?.value || "");

  const buildCard = () => {
    newCardSequence += 1;
    const collapseId = `apartment-layout-fields-new-${Date.now()}-${newCardSequence}`;
    const priceRequestId = `layout-price-request-new-${Date.now()}-${newCardSequence}`;
    const card = document.createElement("div");
    card.className = "card apartment-layout-card border";
    card.dataset.layoutCard = "";
    card.draggable = true;
    card.innerHTML = `
      <div class="card-body p-3">
        <input type="hidden" value="0" data-layout-input="id">
        <input type="hidden" value="" data-layout-input="image_path">
        <input type="file" class="d-none" accept="image/jpeg,image/png,image/webp"
               aria-label="${escapeHtml(text("image"))}" data-layout-file>
        <div class="d-flex flex-column flex-sm-row gap-3">
          <div class="apartment-layout-media">
            <div class="apartment-layout-preview bg-light rounded-3 border" data-layout-preview-box>
              <img src="" alt="" class="d-none" data-layout-preview>
              <i class="bi bi-image text-secondary fs-2" data-layout-preview-empty></i>
            </div>
            <button type="button" class="btn btn-sm btn-outline-primary w-100 mt-2" data-layout-upload-trigger>
              <i class="bi bi-cloud-arrow-up me-1"></i>${escapeHtml(text("choose_image"))}
            </button>
            <div class="form-text text-center" aria-live="polite" data-layout-upload-status>
              ${escapeHtml(text("image_help"))}
            </div>
          </div>
          <div class="flex-grow-1 min-w-0">
            <div class="d-flex flex-wrap align-items-start justify-content-between gap-2">
              <div class="d-flex align-items-start gap-2 min-w-0">
                <button type="button" class="btn btn-sm btn-light apartment-layout-drag"
                        title="${escapeHtml(text("drag"))}" aria-label="${escapeHtml(text("drag"))}" data-layout-drag>
                  <i class="bi bi-grip-vertical"></i>
                </button>
                <div class="min-w-0">
                  <div class="small text-secondary" data-layout-position></div>
                  <div class="fw-semibold text-truncate" data-layout-summary-title>${escapeHtml(text("new_title"))}</div>
                </div>
              </div>
              <div class="d-flex flex-wrap gap-1">
                <button type="button" class="btn btn-sm btn-outline-secondary" data-layout-up
                        title="${escapeHtml(text("move_up"))}"><i class="bi bi-arrow-up"></i></button>
                <button type="button" class="btn btn-sm btn-outline-secondary" data-layout-down
                        title="${escapeHtml(text("move_down"))}"><i class="bi bi-arrow-down"></i></button>
                <button type="button" class="btn btn-sm btn-outline-primary" data-bs-toggle="collapse"
                        data-bs-target="#${collapseId}" aria-expanded="true">
                  <i class="bi bi-pencil me-1"></i>${escapeHtml(text("edit"))}
                </button>
                <button type="button" class="btn btn-sm btn-outline-danger" data-layout-delete>
                  <i class="bi bi-trash me-1"></i>${escapeHtml(text("delete"))}
                </button>
              </div>
            </div>
            <div class="small text-secondary mt-2" data-layout-summary></div>
          </div>
        </div>
        <div class="collapse show mt-3" id="${collapseId}" data-layout-fields>
          <div class="row g-3 border-top pt-3">
            <div class="col-12 col-lg-8">
              <div class="form-floating">
                <input type="text" class="form-control" maxlength="191" placeholder="${escapeHtml(text("title"))}"
                       data-layout-input="title">
                <label>${escapeHtml(text("title"))}</label>
              </div>
            </div>
            <div class="col-6 col-lg-4">
              <div class="form-floating">
                <input type="number" class="form-control" min="0.01" step="0.01" placeholder="0"
                       data-layout-input="area">
                <label>${escapeHtml(text("area"))}</label>
              </div>
            </div>
            <div class="col-6 col-lg-4">
              <div class="form-floating">
                <input type="number" class="form-control" min="0" max="50" step="1" placeholder="0"
                       data-layout-input="rooms">
                <label>${escapeHtml(text("rooms"))}</label>
              </div>
            </div>
            <div class="col-6 col-lg-4">
              <div class="form-floating">
                <input type="number" class="form-control" min="1" max="250" step="1" placeholder="1"
                       data-layout-input="floor_from">
                <label>${escapeHtml(text("floor_from"))}</label>
              </div>
            </div>
            <div class="col-6 col-lg-4">
              <div class="form-floating">
                <input type="number" class="form-control" min="1" max="250" step="1" placeholder="1"
                       data-layout-input="floor_to">
                <label>${escapeHtml(text("floor_to"))}</label>
              </div>
            </div>
            <div class="col-12 col-lg-8">
              <div class="input-group">
                <div class="form-floating">
                  <input type="number" class="form-control" min="0.01" step="0.01" placeholder="0"
                         data-layout-input="price">
                  <label>${escapeHtml(text("price"))}</label>
                </div>
                <span class="input-group-text" data-layout-currency>${escapeHtml(currency())}</span>
              </div>
            </div>
            <div class="col-12">
              <div class="form-check form-switch">
                <input type="checkbox" class="form-check-input" value="1" id="${priceRequestId}"
                       data-layout-input="price_on_request">
                <label class="form-check-label" for="${priceRequestId}">${escapeHtml(text("price_on_request"))}</label>
              </div>
            </div>
            <div class="col-12">
              <div class="form-floating">
                <textarea class="form-control" maxlength="2000" style="height:110px"
                          placeholder="${escapeHtml(text("description"))}" data-layout-input="description"></textarea>
                <label>${escapeHtml(text("description"))}</label>
              </div>
            </div>
          </div>
        </div>
      </div>`;
    return card;
  };

  const applyPriceMode = (card) => {
    const toggle = card.querySelector("[data-layout-input='price_on_request']");
    const price = card.querySelector("[data-layout-input='price']");
    if (!toggle || !price) {
      return;
    }
    if (toggle.checked) {
      price.value = "";
      price.disabled = true;
      price.required = false;
    } else {
      price.disabled = !isSupportedCategory();
      price.required = false;
    }
  };

  const updateSummary = (card) => {
    const title = card.querySelector("[data-layout-input='title']")?.value?.trim() || text("new_title");
    const area = card.querySelector("[data-layout-input='area']")?.value?.trim() || "";
    const rooms = card.querySelector("[data-layout-input='rooms']")?.value?.trim() || "";
    const floorFrom = card.querySelector("[data-layout-input='floor_from']")?.value?.trim() || "";
    const floorTo = card.querySelector("[data-layout-input='floor_to']")?.value?.trim() || "";
    const titleNode = card.querySelector("[data-layout-summary-title]");
    const summaryNode = card.querySelector("[data-layout-summary]");
    if (titleNode) {
      titleNode.textContent = title;
    }
    if (summaryNode) {
      const parts = [];
      if (area) parts.push(`${text("area")}: ${area}`);
      if (rooms) parts.push(`${text("rooms")}: ${rooms}`);
      if (floorFrom) parts.push(`${text("floor_from")}: ${floorTo ? `${floorFrom}–${floorTo}` : floorFrom}`);
      summaryNode.textContent = parts.join(" · ");
      summaryNode.classList.toggle("d-none", parts.length === 0);
    }
    const preview = card.querySelector("[data-layout-preview]");
    if (preview) {
      preview.alt = title;
    }
  };

  const reindex = () => {
    const currentCards = cards();
    currentCards.forEach((card, index) => {
      card.querySelectorAll("[data-layout-input]").forEach((input) => {
        const field = input.dataset.layoutInput;
        input.name = `apartment_layouts[${index}][${field}]`;
      });
      const positionNode = card.querySelector("[data-layout-position]")
        || card.querySelector(".small.text-secondary");
      if (positionNode) {
        positionNode.textContent = text("position").replace(":position", String(index + 1));
      }
      const up = card.querySelector("[data-layout-up]");
      const down = card.querySelector("[data-layout-down]");
      if (up) up.disabled = index === 0;
      if (down) down.disabled = index === currentCards.length - 1;
      card.querySelectorAll("[data-layout-currency]").forEach((node) => {
        node.textContent = currency();
      });
      updateSummary(card);
    });
    empty?.classList.toggle("d-none", currentCards.length > 0);
    if (addButton) {
      addButton.disabled = currentCards.length >= maxLayouts;
    }
  };

  const uploadImage = async (card, fileInput) => {
    const file = fileInput.files?.[0];
    if (!file) {
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      window.alert(text("invalid_image_type"));
      fileInput.value = "";
      return;
    }
    if (file.size > maxImageBytes) {
      window.alert(text("image_too_large"));
      fileInput.value = "";
      return;
    }

    const previousDisabled = fileInput.disabled;
    const uploadTrigger = card.querySelector("[data-layout-upload-trigger]");
    const previewBox = card.querySelector("[data-layout-preview-box]");
    const status = card.querySelector("[data-layout-upload-status]");
    fileInput.disabled = true;
    if (uploadTrigger) uploadTrigger.disabled = true;
    if (status) {
      status.classList.add("text-primary");
      status.textContent = text("uploading");
    }
    try {
      const body = new FormData();
      body.append("image", file);
      body.append("subdir", String(config.upload_subdir || "products/layouts"));
      const response = await fetch(String(config.upload_url || "/uploader/image"), {
        method: "POST",
        credentials: "same-origin",
        body,
      });
      const payload = await response.json();
      if (!response.ok || !payload?.ok || !payload?.url) {
        throw new Error(payload?.error || `HTTP ${response.status}`);
      }
      const pathInput = card.querySelector("[data-layout-input='image_path']");
      const preview = card.querySelector("[data-layout-preview]");
      const previewEmpty = card.querySelector("[data-layout-preview-empty]");
      if (pathInput) pathInput.value = payload.url;
      if (preview) {
        preview.src = payload.main_url || payload.url;
        preview.classList.remove("d-none");
      }
      previewEmpty?.classList.add("d-none");
      previewBox?.classList.remove("is-invalid");
    } catch (error) {
      window.alert(text("upload_failed"));
      previewBox?.classList.add("is-invalid");
    } finally {
      if (status) {
        status.classList.remove("text-primary");
        status.textContent = text("image_help");
      }
      fileInput.disabled = previousDisabled;
      if (uploadTrigger) uploadTrigger.disabled = previousDisabled;
      fileInput.value = "";
    }
  };

  const applyCategoryState = (notify = false) => {
    const supported = isSupportedCategory();
    const hasLayouts = cards().length > 0;
    if (!supported && notify && hasLayouts && !warnedForCategoryChange) {
      window.alert(text("category_warning"));
      warnedForCategoryChange = true;
    }
    if (supported) {
      warnedForCategoryChange = false;
    }
    root.classList.toggle("d-none", !supported);
    root.querySelectorAll("input, textarea, button").forEach((control) => {
      control.disabled = !supported;
    });
    if (supported) {
      cards().forEach(applyPriceMode);
    }
    reindex();
  };

  addButton?.addEventListener("click", () => {
    if (cards().length >= maxLayouts) {
      window.alert(text("limit_reached"));
      return;
    }
    const card = buildCard();
    list?.appendChild(card);
    reindex();
    card.querySelector("[data-layout-upload-trigger]")?.focus();
  });

  list?.addEventListener("click", (event) => {
    const card = event.target.closest("[data-layout-card]");
    if (!card) {
      return;
    }
    if (event.target.closest("[data-layout-upload-trigger]")) {
      card.querySelector("[data-layout-file]")?.click();
      return;
    }
    if (event.target.closest("[data-layout-delete]")) {
      if (window.confirm(text("delete_confirm"))) {
        card.remove();
        reindex();
      }
      return;
    }
    if (event.target.closest("[data-layout-up]")) {
      const previous = card.previousElementSibling;
      if (previous) list.insertBefore(card, previous);
      reindex();
      return;
    }
    if (event.target.closest("[data-layout-down]")) {
      const next = card.nextElementSibling;
      if (next) list.insertBefore(next, card);
      reindex();
    }
  });

  list?.addEventListener("input", (event) => {
    const card = event.target.closest("[data-layout-card]");
    if (card) updateSummary(card);
  });
  list?.addEventListener("change", (event) => {
    const card = event.target.closest("[data-layout-card]");
    if (!card) return;
    if (event.target.matches("[data-layout-file]")) {
      uploadImage(card, event.target);
    }
    if (event.target.matches("[data-layout-input='price_on_request']")) {
      applyPriceMode(card);
    }
  });

  list?.addEventListener("dragstart", (event) => {
    const card = event.target.closest("[data-layout-card]");
    if (!card) {
      event.preventDefault();
      return;
    }
    draggedCard = card;
    card.classList.add("is-dragging");
    event.dataTransfer.effectAllowed = "move";
  });
  list?.addEventListener("dragover", (event) => {
    if (!draggedCard) return;
    event.preventDefault();
    const target = event.target.closest("[data-layout-card]");
    if (!target || target === draggedCard) return;
    const rect = target.getBoundingClientRect();
    list.insertBefore(draggedCard, event.clientY < rect.top + rect.height / 2 ? target : target.nextSibling);
  });
  list?.addEventListener("dragend", () => {
    draggedCard?.classList.remove("is-dragging");
    draggedCard = null;
    reindex();
  });

  const handleCategoryChange = () => {
    applyCategoryState(true);
    window.setTimeout(() => applyCategoryState(false), 0);
  };
  categorySelect.addEventListener("input", handleCategoryChange);
  categorySelect.addEventListener("change", handleCategoryChange);
  form.addEventListener("marketplace-category:changed", handleCategoryChange);
  form.querySelector("[name='currency']")?.addEventListener("change", reindex);
  form.addEventListener("submit", () => {
    if (isSupportedCategory()) reindex();
  });

  cards().forEach((card) => {
    applyPriceMode(card);
    updateSummary(card);
  });
  applyCategoryState(false);
}

function initShortDescriptionCounter(form) {
  const input = document.getElementById("short_desc");
  const counter = document.getElementById("short_desc_counter");

  if (!input || !counter || input.dataset.counterReady === "1") {
    return;
  }

  input.dataset.counterReady = "1";
  const maxLength = input.maxLength > 0 ? input.maxLength : 512;
  const recommendedLength = Number.parseInt(input.dataset.recommendedLength || "220", 10);
  const tooLongMessage = input.dataset.tooLongMessage || `Максимум ${maxLength} символов.`;

  const updateCounter = () => {
    const length = characterLength(input.value);
    const isTooLong = length > maxLength;
    const isLongForCard = !isTooLong && length > recommendedLength;

    counter.textContent = `${length} / ${maxLength}`;
    counter.classList.toggle("text-danger", isTooLong);
    counter.classList.toggle("text-warning", isLongForCard);
    counter.classList.toggle("text-muted", !isTooLong && !isLongForCard);
    input.classList.toggle("is-invalid", isTooLong);
    input.setCustomValidity(isTooLong ? tooLongMessage : "");
  };

  input.addEventListener("input", updateCounter);
  form.addEventListener("submit", () => {
    updateCounter();
    if (!input.checkValidity()) {
      input.focus({ preventScroll: true });
      input.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  });

  updateCounter();
}

function initPublicationSchedule(form) {
  const toggle = form.querySelector("[data-publish-later-toggle]");
  const fields = form.querySelector("[data-publish-schedule-fields]");
  const input = form.querySelector("[data-publish-at]");
  const nowHint = form.querySelector("[data-publish-now-hint]");

  if (!toggle || !fields || !input || toggle.dataset.scheduleReady === "1") {
    return;
  }

  toggle.dataset.scheduleReady = "1";

  const updateVisibility = () => {
    const isScheduled = toggle.checked;
    fields.hidden = !isScheduled;
    input.required = isScheduled;
    if (nowHint) {
      nowHint.hidden = isScheduled;
    }
    if (!isScheduled) {
      input.value = "";
      input.setCustomValidity("");
    }
  };

  toggle.addEventListener("change", updateVisibility);
  form.addEventListener("submit", () => {
    if (!toggle.checked) {
      input.value = "";
    }
  });

  updateVisibility();
}

function initPublicationTimezoneAssistant(form) {
  const root = form.querySelector("[data-publication-timezone-assistant]");
  if (!root || root.dataset.assistantReady === "1") {
    return;
  }
  root.dataset.assistantReady = "1";

  let i18n = {};
  try {
    i18n = JSON.parse(root.dataset.timezoneI18n || "{}");
  } catch (error) {
    i18n = {};
  }

  const companyTimezone = root.dataset.companyTimezone || "Europe/Moscow";
  const companyTimezoneLabel = root.dataset.companyTimezoneLabel || companyTimezone;
  const deviceTimezone = detectedDeviceTimezone();
  const status = root.querySelector("[data-timezone-assistant-status]");
  const companyTime = root.querySelector("[data-company-timezone-time]");
  const deviceTime = root.querySelector("[data-device-timezone-time]");
  const networkLocation = root.querySelector("[data-network-location]");
  const settingsLink = root.querySelector("[data-timezone-settings-link]");
  const preciseButton = root.querySelector("[data-detect-precise-location]");
  const preciseResult = root.querySelector("[data-precise-location-result]");

  const text = (key, replacements = {}) => {
    let value = String(i18n[key] || key);
    Object.entries(replacements).forEach(([name, replacement]) => {
      value = value.split(`:${name}`).join(String(replacement));
    });
    return value;
  };

  const setStatus = (type, message) => {
    if (!status) {
      return;
    }
    status.className = `alert alert-${type} py-2 px-3 my-3 small`;
    status.textContent = message;
  };

  const updateClocks = () => {
    if (companyTime) {
      companyTime.textContent = text("company_time", {
        timezone: companyTimezoneLabel,
        time: formattedTime(companyTimezone),
      });
    }
    if (deviceTime) {
      deviceTime.textContent = text("device_time", {
        timezone: deviceTimezone || "—",
        time: deviceTimezone ? formattedTime(deviceTimezone) : "—",
      });
    }
  };

  const updateSettingsLink = () => {
    if (!settingsLink || !deviceTimezone) {
      return;
    }
    settingsLink.href = `/main/settings?suggest_timezone=${encodeURIComponent(deviceTimezone)}#company-timezone`;
  };

  updateClocks();
  updateSettingsLink();
  window.setInterval(updateClocks, 30000);

  if (deviceTimezone && deviceTimezone === companyTimezone) {
    setStatus("success", text("matched", { timezone: companyTimezoneLabel }));
  } else if (deviceTimezone) {
    setStatus("warning", text("mismatch", {
      device: deviceTimezone,
      company: companyTimezoneLabel,
    }));
  } else {
    setStatus("info", text("network_unavailable"));
  }

  loadNetworkContext(root.dataset.contextUrl || "", deviceTimezone)
    .then((payload) => {
      const network = payload && payload.network ? payload.network : {};
      renderNetworkLocation(networkLocation, network, text);

      if (network.vpn_detected) {
        setStatus("danger", text("vpn_detected", {
          location: networkLocationText(network),
          device: deviceTimezone || "—",
          company: companyTimezoneLabel,
        }));
        return;
      }

      if (network.timezone && deviceTimezone && network.timezone !== deviceTimezone) {
        setStatus("warning", text("possible_vpn", {
          network: network.timezone,
          device: deviceTimezone,
          company: companyTimezoneLabel,
        }));
        return;
      }

      if (deviceTimezone && deviceTimezone !== companyTimezone) {
        setStatus("warning", text("mismatch", {
          device: deviceTimezone,
          company: companyTimezoneLabel,
        }));
        return;
      }

      if (deviceTimezone) {
        setStatus("success", text("vpn_safe_notice", { timezone: companyTimezoneLabel }));
      }
    })
    .catch(() => {
      if (networkLocation) {
        networkLocation.textContent = text("network_unavailable");
        networkLocation.classList.remove("d-none");
      }
    });

  if (preciseButton) {
    preciseButton.addEventListener("click", () => {
      requestPreciseLocation(root, preciseButton, preciseResult, text);
    });
  }
}

function detectedDeviceTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (error) {
    return "";
  }
}

function formattedTime(timezone) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date());
  } catch (error) {
    return "—";
  }
}

async function loadNetworkContext(url, deviceTimezone) {
  if (!url) {
    return {};
  }
  const target = new URL(url, window.location.origin);
  if (deviceTimezone) {
    target.searchParams.set("device_timezone", deviceTimezone);
  }
  const response = await fetch(target.toString(), {
    method: "GET",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Location context failed: ${response.status}`);
  }
  return response.json();
}

function renderNetworkLocation(element, network, text) {
  if (!element) {
    return;
  }
  if (!network || !network.available) {
    element.textContent = text("network_unavailable");
  } else {
    element.textContent = text("network_location", {
      location: networkLocationText(network),
      timezone: network.timezone || "—",
    });
  }
  element.classList.remove("d-none");
}

function networkLocationText(network) {
  return [network.city, network.region, network.country_code].filter(Boolean).join(", ") || "—";
}

function requestPreciseLocation(root, button, result, text) {
  if (!navigator.geolocation) {
    showPreciseResult(result, "text-danger", text("geolocation_error"));
    return;
  }

  const previousHtml = button.innerHTML;
  button.disabled = true;
  button.textContent = text("precise_loading");

  navigator.geolocation.getCurrentPosition(async (position) => {
    try {
      const body = new URLSearchParams({
        csrf_token: root.dataset.csrfToken || "",
        latitude: String(position.coords.latitude),
        longitude: String(position.coords.longitude),
        accuracy: String(position.coords.accuracy || ""),
        device_timezone: detectedDeviceTimezone(),
      });
      const response = await fetch(root.dataset.contextUrl || "/store/products/location-context", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: body.toString(),
      });
      if (!response.ok) {
        throw new Error(`Precise location failed: ${response.status}`);
      }
      const payload = await response.json();
      const precise = payload.precise_location || {};
      const nearest = precise.nearest_city || null;
      if (nearest && nearest.city) {
        showPreciseResult(result, "text-success", text("precise_city", {
          city: nearest.city,
          region: nearest.region || "",
          accuracy: precise.accuracy_m || "—",
        }));
      } else {
        showPreciseResult(result, "text-secondary", text("precise_unknown", {
          accuracy: precise.accuracy_m || "—",
        }));
      }
    } catch (error) {
      showPreciseResult(result, "text-danger", text("geolocation_error"));
    } finally {
      button.disabled = false;
      button.innerHTML = previousHtml;
    }
  }, (error) => {
    const messageKey = error && error.code === error.PERMISSION_DENIED
      ? "geolocation_denied"
      : "geolocation_error";
    showPreciseResult(result, "text-danger", text(messageKey));
    button.disabled = false;
    button.innerHTML = previousHtml;
  }, {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 300000,
  });
}

function showPreciseResult(element, className, message) {
  if (!element) {
    return;
  }
  element.className = `small mt-2 ${className}`;
  element.textContent = message;
}

function productEditorDictionary() {
  const node = document.getElementById("storeProductEditorI18n");
  try {
    return JSON.parse(node?.textContent || "{}");
  } catch (error) {
    return {};
  }
}

function initProductEditorSections(form) {
  const root = form.closest("[data-product-editor]");
  if (!root) return;

  const sectionTargets = {
    main: form.querySelector("#name"),
    description: form.querySelector("#short_desc") || form.querySelector("[name='description']"),
    price: form.querySelector("[name='price']"),
    details: form.querySelector("#specRows") || form.querySelector("#variantRows"),
    fulfillment: form.querySelector("#service-settings-block") || form.querySelector("[name='weight_grams']"),
    categories: form.querySelector("#selectedCategories"),
    publication: form.querySelector("[name='status']"),
    seo: form.querySelector("[name='meta_title']"),
  };
  const observed = [];

  Object.entries(sectionTargets).forEach(([id, field]) => {
    if (!field) return;
    const container = field.closest(".card, [class*='col-']") || field;
    if (!document.getElementById(`product-section-${id}`)) {
      const anchor = document.createElement("span");
      anchor.id = `product-section-${id}`;
      anchor.className = "store-product-section-anchor";
      container.parentNode?.insertBefore(anchor, container);
      observed.push(anchor);
    }
  });
  const media = document.getElementById("product-section-media");
  if (media) observed.push(media);

  const links = Array.from(root.querySelectorAll("[data-product-section-link]"));
  links.forEach((link) => {
    const target = document.querySelector(link.getAttribute("href"));
    if (!target) {
      link.classList.add("d-none");
      return;
    }
    link.addEventListener("click", (event) => {
      event.preventDefault();
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      target.querySelector?.("input, select, textarea, button")?.focus({ preventScroll: true });
    });
  });

  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      const id = visible.target.id.replace("product-section-", "");
      links.forEach((link) => link.classList.toggle("active", link.dataset.productSectionLink === id));
    }, { rootMargin: "-25% 0px -60% 0px", threshold: [0, 0.25, 0.75] });
    observed.forEach((section) => observer.observe(section));
  }
}

function initMobileProductAccordions(form) {
  const headings = Array.from(form.querySelectorAll(".row.g-3 > h6.mt-4"));
  if (!headings.length) return;
  const groups = headings.map((heading, index) => {
    const id = `product-mobile-section-${index + 1}`;
    const elements = [];
    let sibling = heading.nextElementSibling;
    while (sibling && sibling.tagName !== "H6") {
      elements.push(sibling);
      sibling = sibling.nextElementSibling;
    }
    const isPrice = elements.some((element) => element.matches?.("[name='price']") || element.querySelector?.("[name='price']"));
    heading.classList.add("store-product-accordion-heading");
    heading.tabIndex = 0;
    heading.setAttribute("role", "button");
    heading.dataset.mobileAccordion = id;
    heading.dataset.mobileDefaultOpen = isPrice ? "1" : "0";
    if (!heading.querySelector(".store-product-accordion-icon")) {
      const icon = document.createElement("i");
      icon.className = "bi bi-chevron-down store-product-accordion-icon";
      icon.setAttribute("aria-hidden", "true");
      heading.appendChild(icon);
    }
    elements.forEach((element) => {
      element.dataset.mobileAccordionGroup = id;
    });
    return { heading, elements, open: isPrice };
  });

  const media = window.matchMedia("(max-width: 767.98px)");
  const render = (group) => {
    const mobile = media.matches;
    group.heading.setAttribute("aria-expanded", String(!mobile || group.open));
    group.heading.classList.toggle("is-open", !mobile || group.open);
    group.elements.forEach((element) => {
      element.classList.toggle("store-product-accordion-hidden", mobile && !group.open);
    });
  };
  const toggle = (heading) => {
    const group = groups.find((candidate) => candidate.heading === heading);
    if (!group || !media.matches) return;
    group.open = !group.open;
    render(group);
  };
  headings.forEach((heading) => {
    heading.addEventListener("click", () => toggle(heading));
    heading.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggle(heading);
      }
    });
  });
  const rerender = () => groups.forEach(render);
  media.addEventListener?.("change", rerender);
  rerender();
}

function initProductEditorState(form) {
  const root = form.closest("[data-product-editor]");
  if (!root || form.dataset.editorStateReady === "1") return;
  form.dataset.editorStateReady = "1";
  const i18n = productEditorDictionary();
  const status = document.getElementById("productEditorStatus");
  const intentInput = form.querySelector("#productSaveIntent");
  let dirty = false;
  let submitting = false;

  const setDirty = () => {
    if (submitting || dirty) return;
    dirty = true;
    root.dataset.dirty = "1";
    if (status) status.textContent = i18n.unsaved || "Unsaved changes";
  };
  form.addEventListener("input", setDirty);
  form.addEventListener("change", setDirty);
  form.addEventListener("product-editor:dirty", setDirty);

  const name = form.querySelector("#name");
  const slug = form.querySelector("#slug");
  let slugManuallyEdited = Boolean(slug?.value);
  const transliteration = {
    а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i",
    й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r", с: "s", т: "t",
    у: "u", ф: "f", х: "h", ц: "c", ч: "ch", ш: "sh", щ: "sch", ы: "y", э: "e", ю: "yu",
    я: "ya", ъ: "", ь: "",
  };
  const slugify = (value) => String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split("")
    .map((character) => transliteration[character] ?? character)
    .join("")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 191);
  slug?.addEventListener("input", () => {
    slugManuallyEdited = true;
    slug.dataset.manual = "1";
    const normalized = slugify(slug.value);
    if (slug.value !== normalized) slug.value = normalized;
  });
  name?.addEventListener("input", () => {
    if (slug && !slugManuallyEdited) {
      slug.value = slugify(name.value);
    }
  });

  const summary = document.createElement("div");
  summary.className = "alert alert-danger d-none store-product-error-summary";
  summary.setAttribute("role", "alert");
  summary.setAttribute("tabindex", "-1");
  form.prepend(summary);

  const showValidation = () => {
    const invalid = Array.from(form.querySelectorAll(":invalid")).filter((field) => !field.disabled);
    if (!invalid.length) {
      summary.classList.add("d-none");
      return true;
    }
    summary.innerHTML = "";
    const title = document.createElement("div");
    title.className = "fw-semibold";
    title.textContent = String(i18n.validationSummary || "Please fix {count} fields")
      .replace("{count}", String(invalid.length));
    const list = document.createElement("ul");
    list.className = "mb-0 mt-2";
    invalid.forEach((field, index) => {
      const accordionElement = field.closest("[data-mobile-accordion-group]");
      if (accordionElement) {
        const accordionId = accordionElement.dataset.mobileAccordionGroup;
        const heading = form.querySelector(`[data-mobile-accordion="${accordionId}"]`);
        if (heading && heading.getAttribute("aria-expanded") === "false") {
          heading.click();
        }
      }
      if (!field.id) field.id = `product-invalid-${index + 1}`;
      field.classList.add("is-invalid");
      const label = form.querySelector(`label[for="${CSS.escape(field.id)}"]`)?.textContent?.trim()
        || field.getAttribute("aria-label")
        || field.name;
      const item = document.createElement("li");
      const link = document.createElement("a");
      link.href = `#${field.id}`;
      link.textContent = label;
      link.addEventListener("click", (event) => {
        event.preventDefault();
        field.scrollIntoView({ behavior: "smooth", block: "center" });
        field.focus({ preventScroll: true });
      });
      item.appendChild(link);
      list.appendChild(item);
    });
    summary.append(title, list);
    summary.classList.remove("d-none");
    summary.focus();
    invalid[0].focus({ preventScroll: true });
    return false;
  };

  form.addEventListener("submit", (event) => {
    const submitter = event.submitter;
    const intent = submitter?.value || intentInput?.value || (root.dataset.productMode === "add" ? "publish" : "update");
    if (intentInput) intentInput.value = intent;
    if (slug) slug.value = slugify(slug.value || name?.value);
    if (intent !== "draft" && !showValidation()) {
      event.preventDefault();
      return;
    }
    submitting = true;
    dirty = false;
    root.dataset.dirty = "0";
    if (status) status.textContent = i18n.saving || "Saving…";
    form.querySelectorAll("button[type='submit'], input[type='submit']").forEach((button) => {
      button.disabled = true;
    });
  });

  window.addEventListener("beforeunload", (event) => {
    if (!dirty || submitting) return;
    event.preventDefault();
    event.returnValue = "";
  });
  document.addEventListener("click", (event) => {
    const link = event.target.closest("a[href]");
    if (!link || !dirty || submitting || link.target === "_blank" || String(link.getAttribute("href") || "").startsWith("#")) return;
    if (!window.confirm(i18n.leaveConfirm || "Leave without saving?")) {
      event.preventDefault();
    } else {
      submitting = true;
    }
  }, true);
}

function initProductTypeVisibility(form) {
  const type = form.querySelector("#type");
  if (!type || type.dataset.visibilityReady === "1") return;
  type.dataset.visibilityReady = "1";

  const columnsFor = (selector) => Array.from(form.querySelectorAll(selector))
    .map((field) => field.closest("[class*='col-']") || field);
  const service = form.querySelector("#service-settings-block");
  const stock = [
    ...columnsFor("[name='track_stock'],[name='stock_qty'],[name='stock_safety']"),
    form.querySelector("#warehouse-stocks-block"),
  ].filter(Boolean);
  const dimensions = columnsFor("[name='weight_grams'],[name='length_mm'],[name='width_mm'],[name='height_mm']");
  let digitalColumn = form.querySelector("[name='digital_url']")?.closest("[class*='col-']") || null;
  if (!digitalColumn) {
    const seo = form.querySelector("[name='meta_title']")?.closest("[class*='col-']");
    const field = document.createElement("div");
    field.className = "col-12";
    field.innerHTML = `<div class="form-floating"><input type="url" class="form-control" id="digital_url" name="digital_url" placeholder="https://"><label for="digital_url">Digital URL</label></div>`;
    seo?.parentNode?.insertBefore(field, seo);
    digitalColumn = field;
  }

  const toggle = (elements, show) => elements.forEach((element) => {
    element.classList.toggle("d-none", !show);
    element.querySelectorAll?.("input,select,textarea").forEach((field) => {
      field.disabled = !show;
    });
  });
  const apply = () => {
    const value = type.value || "physical";
    toggle(service ? [service] : [], value === "service");
    toggle(stock, value === "physical");
    toggle(dimensions, value === "physical");
    toggle(digitalColumn ? [digitalColumn] : [], value === "digital");
  };
  type.addEventListener("change", apply);
  apply();
}

function initProductMediaManager(form) {
  const root = form.querySelector("[data-product-media]");
  if (!root || root.dataset.mediaReady === "1") return;
  root.dataset.mediaReady = "1";
  const list = root.querySelector("[data-media-list]");
  const input = root.querySelector("[data-media-input]");
  const dropzone = root.querySelector("[data-media-dropzone]");
  const count = root.querySelector("[data-media-count]");
  const empty = root.querySelector("[data-media-empty]");
  const hidden = root.querySelector("[data-media-hidden]");
  const live = root.querySelector("[data-media-live]");
  const mediaI18nNode = document.getElementById("storeProductMediaI18n");
  let i18n = {};
  try {
    i18n = JSON.parse(mediaI18nNode?.textContent || "{}");
  } catch (error) {
    i18n = {};
  }
  const maxItems = Number.parseInt(root.dataset.maxItems || "20", 10);
  const maxBytes = Number.parseInt(root.dataset.maxBytes || String(10 * 1024 * 1024), 10);
  const minSide = Number.parseInt(root.dataset.minSide || "600", 10);
  let replacementTarget = null;

  const items = () => Array.from(list.querySelectorAll("[data-media-item]"));
  const htmlEscape = (value) => String(value || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#039;");

  const itemMarkup = (path = "", preview = "", state = "") => {
    const article = document.createElement("article");
    article.className = "store-product-media-item";
    article.tabIndex = 0;
    article.draggable = true;
    article.dataset.mediaItem = "";
    article.dataset.mediaPath = path;
    article.innerHTML = `
      <div class="store-product-media-preview">
        <img src="${htmlEscape(preview || path)}" alt="">
        <span class="badge rounded-pill text-bg-primary" data-media-position></span>
        <span class="store-product-media-state small" data-media-state>${htmlEscape(state)}</span>
      </div>
      <div class="store-product-media-item-actions">
        <button type="button" class="btn btn-sm btn-light" data-media-action="back" aria-label="${htmlEscape(i18n.moveBack)}"><i class="bi bi-arrow-left"></i></button>
        <button type="button" class="btn btn-sm btn-light" data-media-action="forward" aria-label="${htmlEscape(i18n.moveForward)}"><i class="bi bi-arrow-right"></i></button>
        <button type="button" class="btn btn-sm btn-light" data-media-action="cover">${htmlEscape(i18n.makeCover)}</button>
        <button type="button" class="btn btn-sm btn-light" data-media-action="replace">${htmlEscape(i18n.replace)}</button>
        <button type="button" class="btn btn-sm btn-outline-danger" data-media-action="remove" aria-label="${htmlEscape(i18n.remove)}"><i class="bi bi-trash"></i></button>
      </div>`;
    return article;
  };

  const sync = (focusItem = null, markDirty = true) => {
    const current = items().filter((item) => item.dataset.mediaPath);
    hidden.innerHTML = "";
    const cover = document.createElement("input");
    cover.type = "hidden";
    cover.name = "cover_image";
    cover.value = current[0]?.dataset.mediaPath || "";
    cover.dataset.mediaCover = "";
    hidden.appendChild(cover);
    current.slice(1).forEach((item) => {
      const field = document.createElement("input");
      field.type = "hidden";
      field.name = "gallery[]";
      field.value = item.dataset.mediaPath;
      hidden.appendChild(field);
    });
    items().forEach((item, index, all) => {
      const badge = item.querySelector("[data-media-position]");
      if (badge) badge.textContent = index === 0 ? (i18n.cover || "Cover") : String(index + 1);
      item.classList.toggle("is-cover", index === 0);
      const back = item.querySelector("[data-media-action='back']");
      const forward = item.querySelector("[data-media-action='forward']");
      const coverButton = item.querySelector("[data-media-action='cover']");
      if (back) back.disabled = index === 0;
      if (forward) forward.disabled = index === all.length - 1;
      if (coverButton) coverButton.disabled = index === 0;
    });
    if (count) count.textContent = `${items().length} / ${maxItems}`;
    empty?.classList.toggle("d-none", items().length > 0);
    if (focusItem) focusItem.focus({ preventScroll: true });
    if (markDirty) {
      form.dispatchEvent(new CustomEvent("product-editor:dirty", { bubbles: true }));
    }
  };

  const imageDimensions = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(i18n.badType || "Invalid image"));
    };
    image.src = url;
  });

  const upload = async (file, target = null) => {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) throw new Error(i18n.badType);
    if (file.size > maxBytes) throw new Error(i18n.tooLarge);
    const dimensions = await imageDimensions(file);
    if (Math.min(dimensions.width, dimensions.height) < minSide) throw new Error(i18n.tooSmall);
    const preview = URL.createObjectURL(file);
    const item = target || itemMarkup("", preview, i18n.uploading);
    if (!target) list.appendChild(item);
    const state = item.querySelector("[data-media-state]");
    if (state) state.textContent = i18n.uploading || "";
    try {
      const body = new FormData();
      body.append("image", file);
      body.append("subdir", "products/gallery");
      const response = await fetch(root.dataset.uploadUrl || "/uploader/image", {
        method: "POST",
        credentials: "same-origin",
        body,
      });
      const payload = await response.json();
      if (!response.ok || !payload?.ok || !payload?.url) throw new Error(payload?.error || i18n.uploadFailed);
      item.dataset.mediaPath = payload.url;
      const image = item.querySelector("img");
      if (image) image.src = payload.main_url || payload.url;
      if (state) state.textContent = i18n.ready || "";
      sync(item);
    } finally {
      URL.revokeObjectURL(preview);
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    const available = Math.max(0, maxItems - items().length + (replacementTarget ? 1 : 0));
    if (files.length > available) {
      window.alert(i18n.tooMany || "");
      files.splice(available);
    }
    for (const file of files) {
      const target = replacementTarget;
      replacementTarget = null;
      try {
        await upload(file, target);
      } catch (error) {
        if (target) {
          target.querySelector("[data-media-state]").textContent = error.message || i18n.uploadFailed;
        } else {
          const failed = itemMarkup("", "", error.message || i18n.uploadFailed);
          failed.classList.add("is-error");
          list.appendChild(failed);
          sync(failed);
        }
      }
    }
    input.value = "";
  };

  input?.addEventListener("change", () => uploadFiles(input.files));
  ["dragenter", "dragover"].forEach((eventName) => dropzone?.addEventListener(eventName, (event) => {
    event.preventDefault();
    dropzone.classList.add("is-dragover");
  }));
  ["dragleave", "drop"].forEach((eventName) => dropzone?.addEventListener(eventName, (event) => {
    event.preventDefault();
    dropzone.classList.remove("is-dragover");
  }));
  dropzone?.addEventListener("drop", (event) => uploadFiles(event.dataTransfer?.files));

  list.addEventListener("click", (event) => {
    const button = event.target.closest("[data-media-action]");
    const item = event.target.closest("[data-media-item]");
    if (!button || !item) return;
    const action = button.dataset.mediaAction;
    if (action === "back" && item.previousElementSibling) {
      list.insertBefore(item, item.previousElementSibling);
      sync(item);
    } else if (action === "forward" && item.nextElementSibling) {
      list.insertBefore(item.nextElementSibling, item);
      sync(item);
    } else if (action === "cover") {
      list.prepend(item);
      sync(item);
    } else if (action === "replace") {
      replacementTarget = item;
      input.click();
    } else if (action === "remove" && window.confirm(i18n.removeConfirm || "")) {
      item.remove();
      sync();
    }
  });

  const drake = dragula([list], {
    moves: (element, container, handle) => !handle.closest("button"),
  });
  drake.on("drop", (element) => {
    sync(element);
    const position = items().indexOf(element) + 1;
    if (live) live.textContent = String(i18n.moved || "Moved to position {position}")
      .replace("{position}", String(position));
  });

  sync(null, false);
}

function initCatalogPosition(form) {
  const root = form.querySelector("[data-catalog-position]");
  const mode = root?.querySelector("[data-position-mode]");
  if (!root || !mode) return;
  const after = root.querySelector("[data-position-after]");
  const number = root.querySelector("[data-position-number]");
  const apply = () => {
    after?.classList.toggle("d-none", mode.value !== "after");
    number?.classList.toggle("d-none", mode.value !== "position");
    after?.querySelectorAll("select,input").forEach((field) => {
      field.disabled = mode.value !== "after";
      field.required = mode.value === "after";
    });
    number?.querySelectorAll("select,input").forEach((field) => {
      field.disabled = mode.value !== "position";
      field.required = mode.value === "position";
    });
  };
  mode.addEventListener("change", apply);
  apply();
}
